import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Lightning, Calendar, CreditCard, Bell, ArrowRight, CheckCircle, UserPlus, Key, Users, Play, Pause, CaretDoubleDown, Sparkle, Ruler, Barbell, MapPin, ChatCircleDots, Receipt, QrCode, ShareNetwork, FilePdf, Storefront, ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";

const HERO = "https://static.prod-images.emergentagent.com/jobs/6791b343-2112-41a2-8ad4-0237b5984757/images/75b2e2bb08b9d82224a53725e1b23c1e26387e72ee8df3f0737cbe80e8d4c34c.png";
const TEXTURE = "https://static.prod-images.emergentagent.com/jobs/6791b343-2112-41a2-8ad4-0237b5984757/images/09005f6ceb692665c653e3d6ce651d87daf97bdf3c2ebabb9710fc3f6579f810.png";
const TRAINER_IMG = "https://images.pexels.com/photos/32830368/pexels-photo-32830368.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="landing-page">
      {/* Nav */}
      <header className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3" data-testid="brand-logo">
            <Logo size={32} />
            <div>
              <div className="font-display text-2xl tracking-tight leading-none">TRAINA</div>
              <div className="tracker text-zinc-500 mt-1 hidden sm:block">Run your training business with confidence</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8 tracker">
            <a href="#how" className="hover:text-white">How it works</a>
            <a href="#tour" className="hover:text-white">Tour</a>
            <a href="#program" className="hover:text-white">The Program</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="tracker hover:text-white" data-testid="nav-login">Log in</Link>
            <Link to="/register" className="btn-primary" data-testid="nav-join-cta">
              <span className="hidden sm:inline">Join your coach</span>
              <span className="sm:hidden">Sign up</span>
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section id="hero" data-band="dark" className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/30 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-12 sm:pt-20 lg:pt-24 pb-16 sm:pb-24 lg:pb-32 grid lg:grid-cols-12 gap-8 lg:gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-4 sm:mb-6 flex-wrap">
              <div className="tracker text-brand" data-testid="hero-eyebrow">[ Personal Training · System v2 ]</div>
              <span className="tracker px-2.5 py-1 border border-brand/50 bg-brand/10 text-[#A8B9DC] text-[10px]" data-testid="built-by-coaches-badge">
                Built for busy coaches
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-[9rem] leading-[0.85] mb-6 sm:mb-8" data-testid="hero-title">
              Train smarter.<br />
              <span className="text-brand">Run the business</span><br /><span className="text-brand">like a pro.</span>
            </h1>
            <p className="max-w-2xl text-base sm:text-lg text-zinc-400 leading-relaxed mb-10">
              The Hub for Coaches to manage their existing client base, take on more
              and have Traina cover the rest. Schedule sessions, take payments and
              keep your team in the loop.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/login" className="btn-primary" data-testid="hero-login-cta">Log in <Lightning size={16} weight="fill" /></Link>
              <Link to="/register" className="btn-ghost" data-testid="hero-client-cta">I&apos;m a client — join my coach</Link>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="card-flat p-6 space-y-5" data-testid="hero-stats">
              <div className="tracker">// Live metrics</div>
              <Stat label="Active clients" value="248" delta="+12" />
              <Stat label="Sessions this week" value="36" delta="+4" />
              <Stat label="MRR" value="$8,420" delta="+$1.2k" />
            </div>
          </div>
        </div>

        {/* Scroll cue — nudges visitors toward the onboarding + tour */}
        <a
          href="#how-clients-join"
          className="absolute left-1/2 -translate-x-1/2 bottom-24 sm:bottom-28 lg:bottom-36 flex flex-col items-center gap-2 text-zinc-500 hover:text-white transition-colors group"
          data-testid="hero-scroll-cue"
          aria-label="Scroll to onboarding"
        >
          <span className="tracker text-[10px] opacity-70 group-hover:opacity-100">See how it works</span>
          <CaretDoubleDown size={18} weight="bold" className="animate-bounce" />
        </a>

        {/* Marquee */}
        <div className="relative border-y border-white/10 bg-black overflow-hidden">
          <div className="marquee-track py-4 font-display text-3xl text-zinc-600">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className="flex items-center gap-12 pr-12">
                <span>PLAN</span><span className="text-brand">•</span>
                <span>PREPARE</span><span className="text-brand">•</span>
                <span>RECORD</span><span className="text-brand">•</span>
                <span>RESULTS</span><span className="text-brand">•</span>
                <span>ACHIEVE</span><span className="text-brand">•</span>
                <span>BUILD</span><span className="text-brand">•</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 3-step onboarding flow */}
      <ClientJoinSteps />

      {/* Auto-playing minimalist feature tour */}
      <FeatureTour />

      {/* Features */}
      <section id="how" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-5">
            <div className="tracker mb-4">[ 01 / EVERYTHING YOU RUN A GYM ON ]</div>
            <h2 className="font-display text-4xl sm:text-5xl leading-[0.9]">Built for coaches who actually train people.</h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-zinc-400 text-lg leading-relaxed self-end">
            Scheduling, billing, notifications, coaching, marketing — every module you&apos;d bolt on separately, unified in one command centre.
            You spend less time on admin, and more time on the floor.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-white/10" data-testid="feature-grid">
          <Feature icon={<Calendar size={26} weight="duotone" />} title="Smart Scheduling" body="Book by client, staff or location. Drag to reschedule. Repeat sessions with a custom end date. Holidays block bookings automatically." />
          <Feature icon={<CreditCard size={26} weight="duotone" />} title="Direct Debit & Billing" body="Monthly Session Plans auto-charge via Stripe with pro-rata credits when clients miss. One-off invoices, service packages, VAT support built in." />
          <Feature icon={<Receipt size={26} weight="duotone" />} title="VAT-Ready Invoicing" body="Set your VAT rate + registration number once. Every invoice — monthly, one-off, package — prints the correct breakdown for tax season." />
          <Feature icon={<Bell size={26} weight="duotone" />} title="In-app Notifications" body="Session reminders 1h before start. Onboarding + PR notifs. Bell + toast on every client and trainer page. Email + WhatsApp built in." />

          <Feature icon={<Users size={26} weight="duotone" />} title="Client Portal" body="Clients see their schedule, invoices, holidays and progress. Log body metrics and assessment results. Bot handles reschedules." />
          <Feature icon={<Ruler size={26} weight="duotone" />} title="Body Metrics" body="Weight, body-fat, height + progress photos. Client-side + trainer-side view. Timeline chart to show change over time." />
          <Feature icon={<Barbell size={26} weight="duotone" />} title="Assessment & PR Tracking" body="Bike, dead hang, broad jump, sprint, vertical — pre-built protocol. Baseline vs latest %, roster leaderboard, PR-detection notifications." />
          <Feature icon={<Sparkle size={26} weight="duotone" />} title="AI Business Assistant" body="Claude Sonnet 5 chat bubble on every trainer page. Ask 'how much did I make last month' or 'which clients haven't shown up in 30 days' — instant answers." />

          <Feature icon={<MapPin size={26} weight="duotone" />} title="Multi-Location" body="Studio 1, Online, Client&apos;s Home — book anywhere. Filter calendar and clients by location. Default location per client." />
          <Feature icon={<Storefront size={26} weight="duotone" />} title="Public Landing + Packages" body="Custom branded landing page at /c/your-slug with your service packages. Clients buy + create their account in one flow." />
          <Feature icon={<ShareNetwork size={26} weight="duotone" />} title="Shareable PR Cards" body="One-tap Instagram-ready PNG when a client sets a new PR. Branded, QR-coded — every share funnels leads to your landing page." />
          <Feature icon={<QrCode size={26} weight="duotone" />} title="Scan Analytics" body="Track exactly which client's shares drive scans of your public page. See who's referring the most leads at a glance." />

          <Feature icon={<Users size={26} weight="duotone" />} title="Multi-Trainer Teams" body="Add staff, set their calendar colour + payroll %. Each session inherits their split. Owner dashboard shows per-coach breakdown." />
          <Feature icon={<ChartLineUp size={26} weight="duotone" />} title="Payroll & Earnings" body="Pay staff a % of every session. Weekly auto-payroll, downloadable statements, dedicated 'my earnings' view for coaches." />
          <Feature icon={<FilePdf size={26} weight="duotone" />} title="PDF Exports" body="Branded PDFs for staff statements, monthly invoices, and full assessment histories — hand-off ready for tax time and milestone reviews." />
          <Feature icon={<ChatCircleDots size={26} weight="duotone" />} title="Client-Facing Bot" body="Claude-powered WhatsApp-friendly bot lets clients reschedule, cancel and top-up packages themselves. Cuts admin dead." />
        </div>
      </section>

      {/* About */}
      <section id="program" data-band="dark" className="border-t border-white/10 bg-[#0E0E10]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/5] overflow-hidden border border-white/10">
              <img src={TRAINER_IMG} alt="Trainer with client" className="w-full h-full object-cover grayscale hover:grayscale-0 transition duration-700" />
            </div>
            <div className="absolute -bottom-8 -right-8 card-flat p-6 max-w-xs hidden md:block">
              <div className="tracker text-brand mb-2">// Coach OS</div>
              <div className="font-display text-2xl leading-tight">Less admin.<br />More reps.</div>
            </div>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="tracker mb-4">[ 02 / METHOD ]</div>
            <h2 className="font-display text-4xl sm:text-5xl mb-6 leading-[0.9] text-brand-light" data-testid="method-heading">Your gym, your rules — our infrastructure.</h2>
            <p className="text-zinc-300 leading-relaxed mb-6">
              We don&apos;t build training plans. You do. Traina is the foundation:
              every session, payment, and touchpoint logged so nothing slips.
            </p>
            <ul className="space-y-4 mb-8">
              {[
                "Real-time client schedule + contact info",
                "One-tap session reminders by email",
                "Stripe-powered checkout for invoices",
                "Audit log of every notification sent",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="text-brand-light font-display text-xl mt-[-2px]">/</span>
                  <span className="text-zinc-200">{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/login" className="btn-primary" data-testid="program-cta">Get started <ArrowRight size={16} weight="bold" /></Link>
          </div>
        </div>
      </section>

      {/* Early-access lead form (moved out — now lives after pricing) */}

      {/* Pricing */}
      <section id="pricing" data-band="dark" className="relative overflow-hidden border-t border-white/10">
        <img src={TEXTURE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-[#0A0A0A]/70" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
          <div className="text-center mb-12">
            <div className="tracker mb-6 text-brand">[ Pricing ]</div>
            <h2 className="font-display text-5xl sm:text-7xl mb-4">
              Three plans.<br /><span className="text-brand">Pick your podium.</span>
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              Built for coaches running their own practice.
              <br />
              Start small, scale up as your client base grows.
              <br />
              Cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-[1100px] mx-auto md:items-end" data-testid="pricing-grid">
            {/* Bronze — 3rd place (lowest step) */}
            <div className="card-flat p-8 flex flex-col relative md:mt-0" data-testid="pricing-card-starter">
              <div className="absolute -top-3 left-6 tracker bg-[#0A0A0A] border border-white/20 px-2 py-1 text-[10px]" data-testid="podium-rank-starter">— 3rd · Bronze</div>
              <div className="tracker text-[#B87333] mb-2">BRONZE</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-display text-5xl" data-testid="pricing-amount-starter">£40</span>
                <span className="tracker text-zinc-500">/ mo</span>
              </div>
              <p className="text-zinc-400 text-sm mb-6">Solo coach getting started. Up to 15 clients.</p>
              <ul className="space-y-2.5 mb-8 flex-1">
                {[
                  "Up to 15 clients",
                  "Unlimited sessions & drag-to-reschedule",
                  "Direct Debit + one-off invoices",
                  "VAT-ready invoicing",
                  "In-app + email session reminders",
                  "Body metrics & assessment tracking",
                  "Google / Apple Calendar sync",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <Lightning size={14} weight="fill" className="text-zinc-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/login" className="btn-ghost justify-center" data-testid="pricing-cta-starter">
                Take the Bronze
              </Link>
            </div>

            {/* Gold — 1st place (top of podium) */}
            <div className="card-flat p-10 flex flex-col border-[#FFD700]/50 bg-brand/5 relative shadow-2xl shadow-[#FFD700]/15 md:scale-[1.04] md:-translate-y-6" data-testid="pricing-card-pro">
              <div className="absolute -top-3 left-6 tracker bg-[#FFD700] text-[#0A0A0A] px-2 py-1 text-[10px] font-bold" data-testid="podium-rank-pro">★ 1st · Gold · Most popular</div>
              <div className="tracker text-[#FFD700] mb-2">GOLD</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-display text-7xl text-brand" data-testid="pricing-amount-pro">£50</span>
                <span className="tracker text-zinc-500">/ mo</span>
              </div>
              <p className="text-zinc-400 text-sm mb-6">Growing coach. Up to 50 clients, every feature unlocked.</p>
              <ul className="space-y-2.5 mb-8 flex-1">
                {[
                  "Up to 50 clients",
                  "Everything in Bronze, plus —",
                  "AI Business Assistant (Claude Sonnet 5)",
                  "Client-facing AI bot (reschedule + top-up)",
                  "Service packages + public landing page",
                  "Multi-location + shareable PR cards",
                  "QR scan analytics on shared PRs",
                  "Trainer analytics dashboard + CSV export",
                  "Multi-currency, multi-timezone",
                  "Priority support",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <Lightning size={14} weight="fill" className="text-[#FFD700] mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/login" className="btn-primary justify-center" data-testid="pricing-cta-pro">
                Go for Gold <ArrowRight size={16} weight="bold" />
              </Link>
            </div>

            {/* Silver — 2nd place (middle step) */}
            <div className="card-flat p-8 flex flex-col relative md:-translate-y-3" data-testid="pricing-card-studio">
              <div className="absolute -top-3 left-6 tracker bg-[#0A0A0A] border border-[#C0C0C0]/50 text-[#C0C0C0] px-2 py-1 text-[10px]" data-testid="podium-rank-studio">— 2nd · Silver</div>
              <div className="tracker text-[#C0C0C0] mb-2">SILVER</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-display text-6xl" data-testid="pricing-amount-studio">£60</span>
                <span className="tracker text-zinc-500">/ mo</span>
              </div>
              <p className="text-zinc-400 text-sm mb-6">Multi-coach studio. Unlimited everything.</p>
              <ul className="space-y-2.5 mb-8 flex-1">
                {[
                  "Unlimited clients",
                  "Everything in Gold, plus —",
                  "Multi-trainer team accounts",
                  "Per-trainer commission splits",
                  "Staff payroll & earnings",
                  "Owner dashboard with coach breakdown",
                  "Dedicated onboarding",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <Lightning size={14} weight="fill" className="text-[#C0C0C0] mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/login" className="btn-ghost justify-center" data-testid="pricing-cta-studio">
                Step up to Silver
              </Link>
            </div>
          </div>

          <p className="text-zinc-500 text-xs text-center mt-8">Per coach · No setup fees · Cancel any time</p>

          <div className="flex flex-wrap items-center justify-center gap-4 mt-12">
            <Link to="/login" className="tracker hover:text-white" data-testid="footer-trainer-cta">Trainer Login</Link>
            <span className="text-zinc-700">·</span>
            <Link to="/register" className="tracker hover:text-white" data-testid="footer-client-cta">Client Sign-up</Link>
          </div>
        </div>
      </section>

      {/* Early-access lead form — bottom of page so visitors see pricing first */}
      <EarlyAccessForm />

      <footer className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo size={24} />
            <div>
              <div className="font-display tracking-tight leading-none">TRAINA</div>
              <div className="tracker text-zinc-500 mt-1">Run your training business with confidence</div>
            </div>
          </div>
          <div className="tracker">© {new Date().getFullYear()} · Built for busy coaches</div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, delta }) {
  return (
    <div className="flex items-end justify-between border-b border-white/5 pb-4 last:border-b-0 last:pb-0">
      <div>
        <div className="tracker mb-2">{label}</div>
        <div className="font-display text-3xl leading-none">{value}</div>
      </div>
      <div className="text-brand text-sm font-semibold">{delta}</div>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="p-8 border-r border-b border-white/10 hover:bg-[#141414] transition" data-testid={`feature-${title.toLowerCase()}`}>
      <div className="text-brand mb-6">{icon}</div>
      <div className="font-display text-2xl mb-3">{title}</div>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function ClientJoinSteps() {
  const steps = [
    {
      n: "01",
      Icon: UserPlus,
      title: "Coach signs up",
      body: "Create your trainer account in under 60 seconds. We provision a private workspace just for your practice.",
    },
    {
      n: "02",
      Icon: Key,
      title: "Share your access code",
      body: "Each coach gets a unique 8-character code (e.g. CZJ32VHH). Drop it in a text, an email, or print it on a card at your studio.",
    },
    {
      n: "03",
      Icon: Users,
      title: "Clients join with one tap",
      body: "Your existing clients self-register using your code. They land directly in your calendar — no manual data entry needed.",
    },
  ];

  return (
    <section id="how-clients-join" className="border-t border-white/10" data-testid="client-join-steps">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-5">
            <div className="tracker text-brand mb-4">[ Onboarding ]</div>
            <h2 className="font-display text-4xl sm:text-5xl leading-[0.9]">
              Bring your clients on<br />in 3 steps.
            </h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-zinc-400 text-lg leading-relaxed self-end">
            Traina isn&apos;t a marketplace — we don&apos;t find clients for you.
            You bring your existing book of business in via your private access code.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-0 border border-white/10">
          {steps.map((s, i) => (
            <div
              key={s.n}
              className={`p-8 sm:p-10 ${i < steps.length - 1 ? "md:border-r border-white/10 border-b md:border-b-0" : ""}`}
              data-testid={`join-step-${s.n}`}
            >
              <div className="flex items-baseline justify-between mb-6">
                <span className="font-display text-6xl text-brand">{s.n}</span>
                <s.Icon size={28} weight="duotone" className="text-zinc-500" />
              </div>
              <h3 className="font-display text-2xl mb-3">{s.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TOUR_SLIDES = [
  {
    title: "Schedule sessions in seconds",
    body: "Click-and-drag any slot on the calendar to spin up a session. Set type, price, recurrence — done.",
    tracker: "Calendar · Drag to create",
    mock: "calendar",
  },
  {
    title: "One pot. Every session paid for.",
    body: "Clients top up a prepaid balance. Every session you log auto-debits the pot. No more chasing payments.",
    tracker: "Payments · Auto-billing",
    mock: "pot",
  },
  {
    title: "Reschedule without breaking flow",
    body: "Grab any session and drop it on a new day or time. Recurring sessions, drag-and-drop, undo — built in.",
    tracker: "Calendar · Drag to reschedule",
    mock: "drag",
  },
  {
    title: "Sell packages from your own page",
    body: "Create a service package, share the link. Clients buy on a branded landing page — payments land in your Stripe.",
    tracker: "Packages · Storefront",
    mock: "package",
  },
  {
    title: "Know your numbers cold",
    body: "Trainer analytics with retention, revenue, churn, and CSV export. See what's working — change what isn't.",
    tracker: "Analytics · Dashboard",
    mock: "analytics",
  },
];

const SLIDE_DURATION_MS = 5000;

function FeatureTour() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!playing) return;
    tickRef.current = setTimeout(() => {
      setIdx((i) => (i + 1) % TOUR_SLIDES.length);
    }, SLIDE_DURATION_MS);
    return () => clearTimeout(tickRef.current);
  }, [idx, playing]);

  const slide = TOUR_SLIDES[idx];

  return (
    <section id="tour" data-band="dark" className="border-t border-white/10" data-testid="feature-tour">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="text-center mb-12">
          <div className="tracker text-brand mb-4">[ Feature tour · 25-second walkthrough ]</div>
          <h2 className="font-display text-4xl sm:text-5xl leading-[0.9]">
            See it in motion.
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Mock screen on the left */}
          <div className="lg:col-span-7">
            <div className="card-flat aspect-[16/10] relative overflow-hidden bg-[#0E0E10]" data-testid="tour-screen">
              <div className="absolute inset-0 p-6 sm:p-10 flex items-center justify-center">
                <TourMock kind={slide.mock} />
              </div>
              {/* Decorative grain */}
              <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
              <div className="absolute top-0 left-0 right-0 h-1 bg-white/5">
                <div
                  key={idx}
                  className="h-full bg-brand transition-none"
                  style={{
                    width: playing ? "100%" : "0%",
                    transition: playing ? `width ${SLIDE_DURATION_MS}ms linear` : "none",
                  }}
                  data-testid="tour-progress"
                />
              </div>
            </div>
          </div>

          {/* Text + controls on the right */}
          <div className="lg:col-span-5">
            <div className="tracker text-brand-light mb-3" data-testid="tour-tracker">{slide.tracker}</div>
            <h3 className="font-display text-3xl sm:text-4xl mb-4 leading-tight" data-testid="tour-title">
              {slide.title}
            </h3>
            <p className="text-zinc-400 leading-relaxed mb-8" data-testid="tour-body">
              {slide.body}
            </p>

            {/* Slide dots */}
            <div className="flex items-center gap-2 mb-8" data-testid="tour-dots">
              {TOUR_SLIDES.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`h-1.5 transition-all ${i === idx ? "w-10 bg-brand" : "w-5 bg-white/15 hover:bg-white/30"}`}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`tour-dot-${i}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                className="btn-ghost"
                data-testid="tour-play-toggle"
              >
                {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                {playing ? "Pause" : "Play"}
              </button>
              <span className="tracker text-zinc-600 text-[10px]">{String(idx + 1).padStart(2, "0")} / {String(TOUR_SLIDES.length).padStart(2, "0")}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TourMock({ kind }) {
  if (kind === "calendar") {
    return (
      <div className="w-full h-full grid grid-cols-3 gap-2 animate-in fade-in duration-700">
        {[0, 1, 2].map((c) => (
          <div key={c} className="border border-white/10 bg-[#141414] relative h-full">
            <div className="px-3 py-2 border-b border-white/10 tracker text-[10px] text-zinc-400">
              {["MON", "TUE", "WED"][c]} · {[6, 7, 8][c]}
            </div>
            <div className="relative h-[calc(100%-2rem)] p-1">
              {c === 1 && (
                <div className="absolute left-1 right-1 top-[35%] bg-brand/30 border-l-2 border-brand px-2 py-1.5 animate-pulse">
                  <div className="text-[10px] font-mono text-white">10:00</div>
                  <div className="text-xs font-semibold text-white truncate">Jordan L.</div>
                </div>
              )}
              {c === 2 && (
                <div className="absolute left-1 right-1 top-[20%] bg-emerald-500/15 border-l-2 border-emerald-500/60 px-2 py-1.5">
                  <div className="text-[10px] font-mono text-emerald-300">08:30</div>
                  <div className="text-xs font-semibold text-emerald-200 truncate">Sara V.</div>
                </div>
              )}
              {c === 0 && (
                <div className="absolute left-1 right-1 top-[50%] bg-brand/30 border-l-2 border-brand px-2 py-1.5">
                  <div className="text-[10px] font-mono text-white">14:00</div>
                  <div className="text-xs font-semibold text-white truncate">Maya P.</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "pot") {
    return (
      <div className="w-full max-w-sm space-y-5 animate-in fade-in duration-700">
        <div className="card-flat p-5 bg-[#141414]">
          <div className="tracker text-zinc-500 mb-2">JORDAN&apos;S POT</div>
          <div className="font-display text-5xl text-brand-light">$420.00</div>
          <div className="tracker text-emerald-400 mt-2">+ $200 topped up</div>
        </div>
        <div className="space-y-2">
          {[
            { d: "60 Minutes · Mon 10:00", v: "−$80.00" },
            { d: "60 Minutes · Wed 10:00", v: "−$80.00" },
            { d: "Top-up", v: "+$200.00", emerald: true },
          ].map((r) => (
            <div key={r.d} className="flex items-center justify-between border-b border-white/5 pb-2">
              <span className="text-xs text-zinc-300">{r.d}</span>
              <span className={`font-mono text-xs ${r.emerald ? "text-emerald-400" : "text-zinc-400"}`}>{r.v}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (kind === "drag") {
    return (
      <div className="w-full h-full relative animate-in fade-in duration-700">
        <div className="grid grid-cols-3 gap-2 h-full">
          {[0, 1, 2].map((c) => (
            <div key={c} className="border border-white/10 bg-[#141414] relative">
              <div className="px-3 py-2 border-b border-white/10 tracker text-[10px] text-zinc-400">
                {["MON", "TUE", "WED"][c]}
              </div>
            </div>
          ))}
        </div>
        {/* Original location (ghost) */}
        <div className="absolute left-[6%] top-[40%] w-[28%] bg-brand/15 border-l-2 border-brand/40 px-2 py-1.5 opacity-40">
          <div className="text-[10px] font-mono text-zinc-400">10:00</div>
          <div className="text-xs font-semibold text-zinc-400 truncate">Jordan L.</div>
        </div>
        {/* Dragged-to location */}
        <div className="absolute left-[70%] top-[55%] w-[28%] bg-brand/40 border-l-2 border-brand px-2 py-1.5 ring-2 ring-brand shadow-2xl">
          <div className="text-[10px] font-mono text-white">14:30</div>
          <div className="text-xs font-semibold text-white truncate">Jordan L.</div>
        </div>
        {/* Animated arrow */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          <line x1="20" y1="48" x2="78" y2="60" stroke="var(--brand)" strokeWidth="0.4" strokeDasharray="2,1" />
          <circle cx="78" cy="60" r="0.9" fill="var(--brand)" />
        </svg>
      </div>
    );
  }
  if (kind === "package") {
    return (
      <div className="w-full max-w-md card-flat p-6 bg-[#141414] animate-in fade-in duration-700">
        <div className="tracker text-brand mb-3">[ PUBLIC PACKAGE PAGE ]</div>
        <h4 className="font-display text-3xl mb-2">8-Week Strength Block</h4>
        <p className="text-sm text-zinc-400 mb-5">Twice-weekly 1-on-1s. Programmed for hypertrophy + power.</p>
        <div className="flex items-baseline gap-2 mb-5">
          <span className="font-display text-4xl text-brand-light">£480</span>
          <span className="tracker text-zinc-500">/ 16 sessions</span>
        </div>
        <div className="btn-primary justify-center w-full">Buy with Stripe <ArrowRight size={14} weight="bold" /></div>
      </div>
    );
  }
  if (kind === "analytics") {
    return (
      <div className="w-full max-w-md space-y-4 animate-in fade-in duration-700">
        <div className="grid grid-cols-2 gap-3">
          {[
            { l: "MTD Revenue", v: "$8,420", d: "+18%" },
            { l: "Active Clients", v: "27", d: "+4" },
            { l: "Retention", v: "92%", d: "+3pp" },
            { l: "Avg. Session", v: "$78", d: "−$2" },
          ].map((m) => (
            <div key={m.l} className="card-flat p-3 bg-[#141414]">
              <div className="tracker text-[10px] text-zinc-500 mb-1">{m.l}</div>
              <div className="font-display text-2xl text-white">{m.v}</div>
              <div className="tracker text-brand-light text-[10px] mt-0.5">{m.d}</div>
            </div>
          ))}
        </div>
        <div className="card-flat p-3 bg-[#141414]">
          <div className="tracker text-[10px] text-zinc-500 mb-2">Revenue · last 8 weeks</div>
          <div className="flex items-end gap-1 h-16">
            {[35, 50, 42, 60, 55, 70, 65, 85].map((h, i) => (
              <div
                key={i}
                className="flex-1 bg-gradient-to-t from-brand/40 to-brand"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
  return null;
}

const CLIENT_RANGES = ["1–10", "11–25", "26–50", "50+"];
const PLAN_OPTIONS = [
  { value: "starter", label: "Bronze · £40" },
  { value: "pro", label: "Gold · £50" },
  { value: "studio", label: "Silver · £60" },
  { value: "unsure", label: "Not sure yet" },
];

function EarlyAccessForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [business, setBusiness] = useState("");
  const [clientCount, setClientCount] = useState("");
  const [planInterest, setPlanInterest] = useState("unsure");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      return toast.error("Name and email required");
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        business_name: business.trim() || null,
        client_count: clientCount || null,
        plan_interest: planInterest,
        notes: notes.trim(),
      });
      setDone(true);
      toast.success("Thanks — we'll be in touch.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="early-access" className="border-t border-white/10" data-testid="early-access-section">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="tracker text-brand mb-4">[ 03 / Early access ]</div>
          <h2 className="font-display text-4xl sm:text-5xl mb-6 leading-[0.9]">
            Get in line before<br />the doors open.
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-6 max-w-md">
            Tell us about your coaching practice and we&apos;ll get you set up to bring your
            existing clients onto Traina. No credit card. No pressure.
          </p>
          <ul className="space-y-3 text-sm">
            {[
              "Priority access — skip the public sign-up queue",
              "Bring your existing client base on day one",
              "Founder pricing locked in for life",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <CheckCircle size={16} weight="fill" className="text-brand mt-0.5 flex-shrink-0" />
                <span className="text-zinc-300">{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="card-flat p-6 sm:p-8 space-y-4" data-testid="early-access-form">
            {done ? (
              <div className="text-center py-8" data-testid="early-access-success">
                <CheckCircle size={56} weight="duotone" className="text-brand mx-auto mb-4" />
                <h3 className="font-display text-3xl mb-2">You&apos;re on the list.</h3>
                <p className="text-zinc-400 max-w-sm mx-auto">
                  We&apos;ll be in touch at <span className="text-white font-mono">{email}</span> within
                  24 hours with your access link.
                </p>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="tracker block mb-2">Your name *</label>
                    <input
                      required
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-3 border"
                      placeholder="Coach Alex"
                      data-testid="early-name"
                    />
                  </div>
                  <div>
                    <label className="tracker block mb-2">Email *</label>
                    <input
                      required
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 border"
                      placeholder="you@example.com"
                      data-testid="early-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="tracker block mb-2">Business name <span className="text-zinc-600 normal-case">— optional</span></label>
                  <input
                    type="text"
                    value={business}
                    onChange={(e) => setBusiness(e.target.value)}
                    className="w-full px-4 py-3 border"
                    placeholder="Vital Strength Co."
                    data-testid="early-business"
                  />
                </div>

                <div>
                  <label className="tracker block mb-2">How many clients?</label>
                  <div className="grid grid-cols-4 gap-2" data-testid="early-clientcount">
                    {CLIENT_RANGES.map((r) => (
                      <button
                        type="button"
                        key={r}
                        onClick={() => setClientCount(r)}
                        className={`px-3 py-2 border text-sm tracker ${clientCount === r ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40"}`}
                        data-testid={`early-clients-${r.replace(/[^0-9+]/g, "")}`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="tracker block mb-2">Which plan interests you?</label>
                  <div className="grid grid-cols-2 gap-2">
                    {PLAN_OPTIONS.map((opt) => (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => setPlanInterest(opt.value)}
                        className={`px-3 py-2 border text-xs tracker text-left ${planInterest === opt.value ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40"}`}
                        data-testid={`early-plan-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="tracker block mb-2">Anything we should know? <span className="text-zinc-600 normal-case">— optional</span></label>
                  <textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-4 py-3 border"
                    placeholder="What's your biggest admin headache right now?"
                    data-testid="early-notes"
                  />
                </div>

                <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="early-submit">
                  {submitting ? "Sending…" : "Request early access"} <ArrowRight size={16} weight="bold" />
                </button>
                <p className="tracker text-zinc-600 text-center">We&apos;ll never share your details. Promise.</p>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

